import { useState, useEffect, useRef } from "react";
import { ICON_MD, ICON_LG, HERO_IMG } from "../constants/icons";
import { Link } from "react-router-dom";

/* ─────────────────────────────────────────
   DressWork Landing Page — Mobile-First Rebuild
   Aesthetic: Luxury editorial × warm femininity
───────────────────────────────────────── */

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

function FadeIn({ children, delay = 0, direction = "up", className = "" }) {
  const [ref, visible] = useInView();
  const transforms = { up: "translateY(32px)", down: "translateY(-32px)", left: "translateX(32px)", right: "translateX(-32px)", none: "none" };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : transforms[direction],
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ── Navigation ── */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <nav className={`dw-nav ${scrolled ? "dw-nav--scrolled" : ""}`}>
      <div className="dw-nav__inner">
        <div className="dw-nav__logo">
          <img src={ICON_MD} alt="DW" className="dw-nav__icon" />
          <span className="dw-nav__wordmark">DRESS<strong>WORK</strong></span>
        </div>
        <Link to="/login" className="dw-nav__cta">無料ではじめる</Link>
      </div>
    </nav>
  );
}

/* ── Hero ── */
function Hero() {
  return (
    <section className="dw-hero">
      <div className="dw-hero__bg1" />
      <div className="dw-hero__bg2" />
      <div className="dw-hero__pattern" />
      <div className="dw-hero__gold-line" />

      <div className="dw-hero__bg-img">
        <img src={HERO_IMG} alt="" className="dw-hero__bg-photo" />
      </div>
      <div className="dw-hero__bg-overlay" />

      <div className="dw-hero__content">
        <FadeIn delay={0.1}>
          <div className="dw-hero__badge">COMING SOON — 2026</div>
        </FadeIn>
        <FadeIn delay={0.25}>
          <h1 className="dw-hero__title">
            あなたの
            <span className="dw-hero__title-gold">新しいステージへ</span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.4}>
          <p className="dw-hero__subtitle">
            チャットで希望を伝えるだけ。<br />
            あなたにぴったりのお店を専任スタッフが厳選してご紹介。<br />
            未経験でも安心のサポート体制。
          </p>
        </FadeIn>
        <FadeIn delay={0.55}>
          <div className="dw-hero__actions">
            <a href="/login" className="dw-hero__btn">無料ではじめる <span>→</span></a>
            <span className="dw-hero__hint">ダウンロード無料・登録30秒</span>
          </div>
        </FadeIn>
        <FadeIn delay={0.7}>
          <div className="dw-hero__stats">
            {[
              { num: "500+", label: "掲載店舗数" },
              { num: "98%", label: "利用者満足度" },
              { num: "30秒", label: "登録完了まで" },
            ].map((s, i) => (
              <div key={i} className="dw-hero__stat">
                <div className="dw-hero__stat-num">{s.num}</div>
                <div className="dw-hero__stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Features ── */
function Features() {
  const features = [
    { num: "01", title: "チャットで簡単", desc: "面倒な履歴書は不要。AIチャットがあなたの希望をヒアリング。数分で条件が整理されます。" },
    { num: "02", title: "専任スタッフがご紹介", desc: "AIが条件を整理したら、業界に詳しい専任スタッフが厳選した求人を2〜3件ご紹介します。" },
    { num: "03", title: "完全プライバシー保護", desc: "個人情報は厳重に管理。お店への紹介時も、あなたの許可なく情報を共有することはありません。" },
    { num: "04", title: "未経験でも安心", desc: "経験ゼロからでも大丈夫。未経験歓迎のお店や、研修制度が充実した店舗を優先的にご紹介。" },
  ];

  return (
    <section className="dw-features">
      <div className="dw-features__deco" />
      <div className="dw-container">
        <FadeIn>
          <div className="dw-sh">
            <div className="dw-sh__tag">HOW IT WORKS</div>
            <h2 className="dw-sh__title">使い方は<span className="dw-gold">とてもシンプル</span></h2>
          </div>
        </FadeIn>
        <div className="dw-features__grid">
          {features.map((f, i) => (
            <FadeIn key={i} delay={0.1 + i * 0.1}>
              <div className="dw-fcard">
                <div className="dw-fcard__num">{f.num}<div className="dw-fcard__numline" /></div>
                <h3 className="dw-fcard__title">{f.title}</h3>
                <p className="dw-fcard__desc">{f.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.3}>
          <div className="dw-steps">
            {[
              { step: "01", label: "アプリをダウンロード" },
              { step: "02", label: "チャットで条件を入力" },
              { step: "03", label: "厳選求人をご紹介" },
            ].map((s, i) => (
              <div key={i} className="dw-steps__wrap">
                <div className="dw-steps__item">
                  <div className="dw-steps__num">{s.step}</div>
                  <div className="dw-steps__label">{s.label}</div>
                </div>
                {i < 2 && <div className="dw-steps__line" />}
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Testimonials ── */
function Testimonials() {
  const reviews = [
    { name: "ゆうな", age: "22歳", badge: "未経験スタート", text: "登録してすぐにチャットで条件を伝えたら、翌日には3つのお店を紹介してもらえました。初めてで不安だったけど、スタッフさんが丁寧に相談に乗ってくれて安心でした。" },
    { name: "あいり", age: "25歳", badge: "経験者", text: "他のサイトだと情報が多すぎて迷うけど、ドレスワークは自分に合ったところだけ紹介してくれるから効率的。今のお店はここで見つけました。" },
    { name: "みく", age: "20歳", badge: "未経験スタート", text: "面接の同行もしてくれて、体入の段取りまで全部やってくれました。自分ひとりじゃ絶対無理だったと思います。本当に感謝してます。" },
  ];

  return (
    <section className="dw-testi">
      <div className="dw-testi__pattern" />
      <div className="dw-container dw-testi__inner">
        <FadeIn>
          <div className="dw-sh dw-sh--dark">
            <div className="dw-sh__tag">VOICES</div>
            <h2 className="dw-sh__title">利用者の<span className="dw-gold">声</span></h2>
          </div>
        </FadeIn>
        <div className="dw-testi__grid">
          {reviews.map((r, i) => (
            <FadeIn key={i} delay={0.1 + i * 0.1}>
              <div className="dw-rcard">
                <div className="dw-rcard__stars">★★★★★</div>
                <p className="dw-rcard__text">"{r.text}"</p>
                <div className="dw-rcard__author">
                  <div className="dw-rcard__avatar">{r.name[0]}</div>
                  <div>
                    <div className="dw-rcard__name">{r.name}さん（{r.age}）</div>
                    <div className="dw-rcard__badge">{r.badge}</div>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── FAQ ── */
function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "本当に無料で使えますか？", a: "はい、ダウンロードから求人紹介まで、利用者様は完全無料です。費用は一切かかりません。" },
    { q: "未経験でも大丈夫ですか？", a: "もちろんです。利用者の約6割が未経験からのスタートです。未経験歓迎のお店や、研修制度が整ったお店を優先的にご紹介します。" },
    { q: "個人情報は安全ですか？", a: "お預かりした情報は厳重に管理し、求人紹介の目的以外には一切使用しません。お店への紹介時にも、あなたの許可なく情報を共有することはありません。" },
    { q: "紹介されたお店を断ることはできますか？", a: "もちろんです。ご紹介した求人に必ず応募する必要はありません。気に入ったお店がなければ、条件を変えて再度ご紹介することも可能です。" },
    { q: "どのエリアの求人がありますか？", a: "現在は都内主要エリア（新宿・歌舞伎町、六本木・西麻布、銀座、渋谷、池袋）に加え、大阪・京都エリアでも500店舗以上の求人を掲載しています。今後さらにエリアを拡大予定です。" },
  ];

  return (
    <section className="dw-faq">
      <div className="dw-faq__inner">
        <FadeIn>
          <div className="dw-sh">
            <div className="dw-sh__tag">FAQ</div>
            <h2 className="dw-sh__title">よくある<span className="dw-gold">ご質問</span></h2>
          </div>
        </FadeIn>
        <div className="dw-faq__list">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <FadeIn key={i} delay={0.04 + i * 0.04}>
                <div className={`dw-fqi ${isOpen ? "dw-fqi--open" : ""}`} onClick={() => setOpen(isOpen ? null : i)}>
                  <div className="dw-fqi__head">
                    <span className="dw-fqi__q">{f.q}</span>
                    <span className="dw-fqi__icon">+</span>
                  </div>
                  <div className="dw-fqi__body">
                    <div className="dw-fqi__a">{f.a}</div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── CTA ── */
function CTA() {
  return (
    <section id="download" className="dw-cta">
      <div className="dw-cta__glow" />
      <div className="dw-cta__inner">
        <FadeIn><img src={ICON_LG} alt="DW" className="dw-cta__icon" /></FadeIn>
        <FadeIn delay={0.15}><h2 className="dw-cta__title">今すぐ、<span className="dw-gold">新しい一歩</span>を</h2></FadeIn>
        <FadeIn delay={0.25}><p className="dw-cta__sub">登録は30秒、利用は完全無料。<br />あなたにぴったりのお店が見つかります。</p></FadeIn>
        <FadeIn delay={0.35}>
          <div className="dw-cta__btns">
            <a href="#download" className="dw-store">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 16.56 2.93 11.3 4.7 7.72C5.57 5.93 7.36 4.81 9.28 4.77C10.55 4.74 11.76 5.64 12.55 5.64C13.33 5.64 14.8 4.56 16.33 4.72C17 4.75 18.83 5 20.03 6.67C19.91 6.74 17.67 8.03 17.69 10.82C17.72 14.13 20.53 15.19 20.56 15.2C20.53 15.28 20.1 16.79 19.03 18.34L18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/></svg>
              <div className="dw-store__text"><small>Download on the</small><span>App Store</span></div>
            </a>
            <a href="#download" className="dw-store">
              <svg width="18" height="20" viewBox="0 0 20 22" fill="currentColor"><path d="M1 1.48C.85 1.72.78 2.02.78 2.37V19.63c0 .35.07.65.22.89l.08.08L10.6 11.08v-.16L1.08 1.4 1 1.48Z"/><path d="M13.78 14.27 10.6 11.08v-.16l3.18-3.19.1.06 3.7 2.08c1.06.59 1.06 1.57 0 2.17l-3.7 2.17-.1.06Z" opacity=".8"/><path d="m13.88 14.21-3.28-3.28L1 20.52c.36.38.94.42 1.6.05l11.28-6.36Z" opacity=".6"/><path d="M13.88 7.79 2.6 1.43C1.94 1.06 1.36 1.1 1 1.48l9.6 9.59 3.28-3.28Z" opacity=".9"/></svg>
              <div className="dw-store__text"><small>GET IT ON</small><span>Google Play</span></div>
            </a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Footer ── */
function Footer() {
  return (
    <footer className="dw-footer">
      <div className="dw-footer__inner">
        <div className="dw-footer__logo">DRESS<strong>WORK</strong></div>
        <div className="dw-footer__links">
          <a href="/terms" className="dw-footer__link">利用規約</a>
          <a href="/privacy" className="dw-footer__link">プライバシーポリシー</a>
          <a href="mailto:gk3gogogo@gmail.com" className="dw-footer__link">お問い合わせ</a>
          <span className="dw-footer__link">合同会社GK3</span>
        </div>
        <div className="dw-footer__copy">© 2026 DressWork. All rights reserved.</div>
      </div>
    </footer>
  );
}

/* ── Main ── */
export default function LandingPage() {
  return (
    <div className="dw-root">
      <Nav /><Hero /><Features /><Testimonials /><FAQ /><CTA /><Footer />
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Noto+Serif+JP:wght@300;400;500;600&family=Zen+Kaku+Gothic+New:wght@400;500;700&family=Noto+Sans+JP:wght@400;500;600;700&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{background:#1a1416}
::selection{background:rgba(212,175,110,.3);color:#f5efe6}

:root{
  --gold:#d4af6e;--gold-dk:#c49a50;--gold-lt:#f0d48a;--gold-dp:#a67c2e;
  --ivory:#f5efe6;--dark:#1a1416;--dark-m:#2a2024;
  --txt:#2a2024;--txt-m:#7a6a6e;
  --fd:'Cormorant Garamond','Noto Serif JP',serif;
  --fb:'Zen Kaku Gothic New','Noto Sans JP',-apple-system,sans-serif;
}

.dw-root{overflow-x:hidden;font-family:var(--fb)}
.dw-container{max-width:1100px;margin:0 auto;padding:0 20px}
.dw-gold{color:var(--gold-dp);font-weight:600}

/* Section Header */
.dw-sh{text-align:center;margin-bottom:40px}
.dw-sh__tag{font-size:11px;color:var(--gold-dk);font-weight:600;letter-spacing:.2em;margin-bottom:10px}
.dw-sh__title{font-size:26px;font-weight:300;color:var(--txt);font-family:var(--fd);letter-spacing:.06em}
.dw-sh--dark .dw-sh__tag{color:rgba(212,175,110,.6)}
.dw-sh--dark .dw-sh__title{color:var(--ivory)}
.dw-sh--dark .dw-gold{color:var(--gold)}

/* ═══ NAV ═══ */
.dw-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:14px 0;transition:all .4s ease}
.dw-nav--scrolled{padding:10px 0;background:rgba(26,20,22,.92);backdrop-filter:blur(20px);border-bottom:1px solid rgba(212,175,110,.1)}
.dw-nav__inner{max-width:1100px;margin:0 auto;padding:0 16px;display:flex;align-items:center;justify-content:space-between}
.dw-nav__logo{display:flex;align-items:center;gap:8px}
.dw-nav__icon{width:30px;height:30px;border-radius:8px;box-shadow:0 2px 10px rgba(212,175,110,.25);flex-shrink:0;object-fit:cover}
.dw-nav__wordmark{font-size:16px;font-weight:300;letter-spacing:.12em;font-family:var(--fd);color:var(--ivory)}
.dw-nav__wordmark strong{font-weight:600}
.dw-nav__cta{padding:8px 16px;border-radius:22px;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:11px;font-weight:700;text-decoration:none;letter-spacing:.04em;white-space:nowrap;flex-shrink:0}

/* ═══ HERO ═══ */
.dw-hero{min-height:100vh;min-height:100dvh;position:relative;overflow:hidden;display:flex;align-items:center;background:linear-gradient(170deg,var(--dark) 0%,var(--dark-m) 40%,#1e181c 100%)}
.dw-hero__bg1{position:absolute;top:5%;right:-15%;width:250px;height:250px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,110,.06) 0%,transparent 70%);pointer-events:none}
.dw-hero__bg2{position:absolute;bottom:5%;left:-15%;width:200px;height:200px;border-radius:50%;background:radial-gradient(circle,rgba(196,154,80,.04) 0%,transparent 70%);pointer-events:none}
.dw-hero__pattern{position:absolute;inset:0;background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23d4af6e' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");pointer-events:none;opacity:.5}
.dw-hero__gold-line{display:none}
.dw-hero__content{max-width:1100px;margin:0 auto;padding:90px 20px 50px;position:relative;z-index:3;width:100%;display:flex;flex-direction:column;align-items:center;text-align:center}
.dw-hero__bg-img{position:absolute;inset:0;z-index:0}
.dw-hero__bg-photo{width:100%;height:100%;object-fit:cover;object-position:center 20%}
.dw-hero__bg-overlay{position:absolute;inset:0;z-index:1;background:linear-gradient(180deg,rgba(26,20,22,.75) 0%,rgba(26,20,22,.65) 100%)}
.dw-hero__badge{display:inline-block;padding:5px 12px;border-radius:18px;border:1px solid rgba(212,175,110,.25);background:rgba(212,175,110,.06);font-size:10px;color:var(--gold);font-weight:600;letter-spacing:.1em;margin-bottom:20px}
.dw-hero__title{font-size:32px;font-weight:300;color:var(--ivory);line-height:1.3;font-family:var(--fd);letter-spacing:.04em;margin-bottom:14px}
.dw-hero__title-gold{display:block;font-weight:600;background:linear-gradient(135deg,var(--gold-lt),var(--gold),var(--gold-dk));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.dw-hero__subtitle{font-size:13.5px;color:rgba(245,239,230,.5);line-height:1.85;margin-bottom:32px;font-weight:400}
.dw-hero__actions{display:flex;flex-direction:column;gap:10px;align-items:center}
.dw-hero__btn{padding:14px 28px;border-radius:28px;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:14px;font-weight:700;text-decoration:none;letter-spacing:.04em;box-shadow:0 4px 20px rgba(212,175,110,.3);display:inline-flex;align-items:center;gap:6px;font-family:var(--fb)}
.dw-hero__btn span{font-size:16px}
.dw-hero__hint{font-size:11px;color:rgba(212,175,110,.4);font-weight:500}
.dw-hero__stats{display:flex;gap:24px;margin-top:40px;justify-content:center}
.dw-hero__stat-num{font-size:24px;font-weight:300;font-family:var(--fd);color:var(--gold);letter-spacing:.02em}
.dw-hero__stat-label{font-size:10px;color:rgba(245,239,230,.3);font-weight:500;letter-spacing:.06em;margin-top:2px}

/* ═══ FEATURES ═══ */
.dw-features{padding:64px 0;position:relative;background:linear-gradient(180deg,#faf6f0,#f5efe6)}
.dw-features__deco{position:absolute;right:8%;top:0;bottom:0;width:1px;background:linear-gradient(180deg,transparent,rgba(212,175,110,.12),transparent);pointer-events:none}
.dw-features__grid{display:grid;grid-template-columns:1fr;gap:14px}
.dw-fcard{padding:24px 20px;border-radius:16px;background:rgba(255,255,255,.7);border:1px solid rgba(212,175,110,.1);height:100%}
.dw-fcard__num{font-size:30px;font-weight:300;font-family:var(--fd);color:var(--gold);letter-spacing:.04em;margin-bottom:12px;line-height:1;position:relative;display:inline-block}
.dw-fcard__numline{position:absolute;bottom:-3px;left:0;width:22px;height:1px;background:linear-gradient(90deg,var(--gold),transparent)}
.dw-fcard__title{font-size:15px;font-weight:700;color:var(--txt);margin-bottom:6px;letter-spacing:.02em}
.dw-fcard__desc{font-size:13px;color:var(--txt-m);line-height:1.8;font-weight:400}

.dw-steps{margin-top:48px;display:flex;flex-direction:column;align-items:center;gap:0}
.dw-steps__wrap{display:flex;flex-direction:column;align-items:center}
.dw-steps__item{text-align:center}
.dw-steps__num{font-size:22px;font-weight:300;font-family:var(--fd);color:var(--gold);letter-spacing:.04em}
.dw-steps__label{font-size:12px;color:#5a4a4e;font-weight:600;margin-top:3px;letter-spacing:.02em}
.dw-steps__line{width:1px;height:20px;background:linear-gradient(180deg,rgba(212,175,110,.3),rgba(212,175,110,.08));margin:8px 0}

/* ═══ TESTIMONIALS ═══ */
.dw-testi{padding:64px 0;position:relative;background:linear-gradient(170deg,#1e181c,var(--dark-m),var(--dark))}
.dw-testi__pattern{position:absolute;inset:0;background:url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4af6e' fill-opacity='0.015'%3E%3Cpath fill-rule='evenodd' d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");pointer-events:none}
.dw-testi__inner{position:relative;z-index:2}
.dw-testi__grid{display:grid;grid-template-columns:1fr;gap:14px}
.dw-rcard{padding:24px 20px;border-radius:16px;background:rgba(255,255,255,.04);border:1px solid rgba(212,175,110,.08);backdrop-filter:blur(10px);height:100%;display:flex;flex-direction:column}
.dw-rcard__stars{margin-bottom:12px;font-size:12px;letter-spacing:2px;color:var(--gold)}
.dw-rcard__text{font-size:13px;color:rgba(245,239,230,.65);line-height:1.85;font-weight:400;flex:1;margin-bottom:16px}
.dw-rcard__author{display:flex;align-items:center;gap:10px}
.dw-rcard__avatar{width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,rgba(212,175,110,.2),rgba(196,154,80,.1));display:flex;align-items:center;justify-content:center;font-size:13px;color:var(--gold);font-weight:600;font-family:var(--fd);flex-shrink:0}
.dw-rcard__name{font-size:12px;color:var(--ivory);font-weight:600}
.dw-rcard__badge{font-size:10px;color:rgba(212,175,110,.5);font-weight:500}

/* ═══ FAQ ═══ */
.dw-faq{padding:64px 0;background:linear-gradient(180deg,#faf6f0,#f5efe6)}
.dw-faq__inner{max-width:720px;margin:0 auto;padding:0 20px}
.dw-faq__list{display:flex;flex-direction:column;gap:8px}
.dw-fqi{border-radius:14px;overflow:hidden;cursor:pointer;background:rgba(255,255,255,.5);border:1px solid rgba(212,175,110,.06);transition:all .3s ease}
.dw-fqi--open{background:rgba(255,255,255,.9);border-color:rgba(212,175,110,.2);box-shadow:0 4px 20px rgba(212,175,110,.08)}
.dw-fqi__head{padding:14px 16px;display:flex;justify-content:space-between;align-items:center;gap:10px}
.dw-fqi__q{font-size:13.5px;font-weight:600;color:var(--txt);line-height:1.5}
.dw-fqi__icon{font-size:18px;color:var(--gold-dk);transition:transform .3s ease;flex-shrink:0;line-height:1}
.dw-fqi--open .dw-fqi__icon{transform:rotate(45deg)}
.dw-fqi__body{max-height:0;overflow:hidden;transition:max-height .4s cubic-bezier(.22,1,.36,1)}
.dw-fqi--open .dw-fqi__body{max-height:250px}
.dw-fqi__a{padding:0 16px 14px;font-size:13px;color:var(--txt-m);line-height:1.85;font-weight:400}

/* ═══ CTA ═══ */
.dw-cta{padding:64px 0;background:linear-gradient(170deg,var(--dark),var(--dark-m),#1e181c);position:relative;text-align:center}
.dw-cta__glow{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,110,.05) 0%,transparent 70%);pointer-events:none}
.dw-cta__inner{max-width:500px;margin:0 auto;padding:0 20px;position:relative;z-index:2}
.dw-cta__icon{width:56px;height:56px;border-radius:18px;margin:0 auto 20px;box-shadow:0 8px 32px rgba(212,175,110,.25);object-fit:cover}
.dw-cta__title{font-size:24px;font-weight:300;color:var(--ivory);font-family:var(--fd);letter-spacing:.06em;margin-bottom:12px}
.dw-cta__sub{font-size:13px;color:rgba(245,239,230,.45);line-height:1.85;margin-bottom:32px;font-weight:400}
.dw-cta__btns{display:flex;flex-direction:column;gap:10px;align-items:center}
.dw-store{display:inline-flex;align-items:center;gap:10px;padding:12px 22px;border-radius:12px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);color:var(--ivory);text-decoration:none;transition:all .3s ease;backdrop-filter:blur(10px);width:100%;max-width:220px;justify-content:center}
.dw-store__text{text-align:left}
.dw-store__text small{font-size:9px;opacity:.6;display:block;font-weight:500}
.dw-store__text span{font-size:14px;font-weight:600;display:block;margin-top:1px}

/* ═══ FOOTER ═══ */
.dw-footer{padding:32px 0 24px;background:#141012;border-top:1px solid rgba(212,175,110,.06)}
.dw-footer__inner{max-width:1100px;margin:0 auto;padding:0 20px;text-align:center}
.dw-footer__logo{font-size:14px;font-weight:300;letter-spacing:.15em;font-family:var(--fd);color:rgba(245,239,230,.3);margin-bottom:12px}
.dw-footer__logo strong{font-weight:600}
.dw-footer__links{display:flex;justify-content:center;gap:14px;flex-wrap:wrap;margin-bottom:14px}
.dw-footer__link{font-size:11px;color:rgba(245,239,230,.25);text-decoration:none;font-weight:500;transition:color .2s}
.dw-footer__link:hover{color:rgba(212,175,110,.6)}
.dw-footer__copy{font-size:10px;color:rgba(245,239,230,.15);letter-spacing:.04em}


/* ════════════════════════════════════════
   TABLET  640px+
   ════════════════════════════════════════ */
@media(min-width:640px){
  .dw-container,.dw-faq__inner{padding:0 28px}
  .dw-nav__inner{padding:0 28px}
  .dw-nav{padding:18px 0}
  .dw-nav--scrolled{padding:12px 0}
  .dw-nav__icon{width:34px;height:34px;border-radius:10px}
  .dw-nav__wordmark{font-size:18px}
  .dw-nav__cta{padding:9px 22px;font-size:12px}

  .dw-hero__bg-photo{object-position:right 25%}
  .dw-hero__content{padding:120px 28px 80px;align-items:flex-start;text-align:left}
  .dw-hero__actions{align-items:flex-start}
  .dw-hero__stats{justify-content:flex-start}
  .dw-hero__bg-overlay{background:linear-gradient(90deg,rgba(26,20,22,.88) 0%,rgba(26,20,22,.78) 35%,rgba(26,20,22,.45) 65%,rgba(26,20,22,.2) 100%)}
  .dw-hero__badge{font-size:11px;padding:6px 16px}
  .dw-hero__title{font-size:48px}
  .dw-hero__subtitle{font-size:15px}
  .dw-hero__actions{flex-direction:row;align-items:center}
  .dw-hero__btn{padding:15px 32px;font-size:15px}
  .dw-hero__stats{gap:36px;margin-top:56px}
  .dw-hero__stat-num{font-size:30px}
  .dw-hero__stat-label{font-size:11px}
  .dw-hero__gold-line{display:block;position:absolute;left:12%;top:15%;bottom:15%;width:1px;background:linear-gradient(180deg,transparent,rgba(212,175,110,.2),transparent);pointer-events:none}

  .dw-features,.dw-testi{padding:100px 0}
  .dw-features__grid{grid-template-columns:repeat(2,1fr);gap:20px}
  .dw-fcard{padding:32px 24px}
  .dw-fcard__num{font-size:34px}
  .dw-fcard__title{font-size:16px}

  .dw-steps{flex-direction:row;gap:0;justify-content:center;align-items:center}
  .dw-steps__wrap{flex-direction:row;align-items:center}
  .dw-steps__line{width:40px;height:1px;background:linear-gradient(90deg,rgba(212,175,110,.3),rgba(212,175,110,.08));margin:0 14px}

  .dw-testi__grid{grid-template-columns:repeat(2,1fr);gap:20px}
  .dw-rcard{padding:28px 24px}

  .dw-sh{margin-bottom:56px}
  .dw-sh__title{font-size:32px}

  .dw-faq,.dw-cta{padding:100px 0}
  .dw-fqi__head{padding:18px 22px}
  .dw-fqi__q{font-size:14.5px}
  .dw-fqi__a{padding:0 22px 18px;font-size:13.5px}

  .dw-cta__icon{width:68px;height:68px;border-radius:20px}
  .dw-cta__title{font-size:30px}
  .dw-cta__btns{flex-direction:row}
  .dw-store{padding:14px 26px}
}


/* ════════════════════════════════════════
   DESKTOP  1024px+
   ════════════════════════════════════════ */
@media(min-width:1024px){
  .dw-hero__bg-photo{object-position:right 20%}
  .dw-hero__title{font-size:clamp(52px,7vw,68px)}
  .dw-hero__subtitle{font-size:16px;max-width:480px}
  .dw-hero__stats{gap:44px;margin-top:64px}
  .dw-hero__stat-num{font-size:34px}
  .dw-hero__stat-label{font-size:11.5px}
  .dw-hero__bg1{right:5%;width:400px;height:400px}

  .dw-features__grid{grid-template-columns:repeat(4,1fr);gap:24px}
  .dw-fcard{padding:36px 28px}
  .dw-fcard__num{font-size:36px}
  .dw-fcard__title{font-size:17px}
  .dw-fcard__desc{font-size:13.5px}

  .dw-testi__grid{grid-template-columns:repeat(3,1fr)}
  .dw-rcard{padding:32px 26px}

  .dw-sh__title{font-size:clamp(30px,4vw,38px)}

  .dw-cta__icon{width:72px;height:72px;border-radius:22px}
  .dw-cta__title{font-size:36px}
}


/* ════════════════════════════════════════
   SMALL PHONE  ≤ 380px
   ════════════════════════════════════════ */
@media(max-width:380px){
  .dw-hero__content{padding:80px 16px 40px}
  .dw-hero__title{font-size:28px}
  .dw-hero__subtitle{font-size:13px}
  .dw-hero__stats{gap:18px}
  .dw-hero__stat-num{font-size:20px}
  .dw-hero__btn{padding:13px 22px;font-size:13px}
  .dw-nav__inner{padding:0 12px}
  .dw-nav__cta{padding:7px 12px;font-size:10px}
  .dw-nav__wordmark{font-size:14px;letter-spacing:.08em}
  .dw-nav__icon{width:28px;height:28px;border-radius:7px}
  .dw-footer__links{gap:10px}
  .dw-store{padding:11px 16px;max-width:200px}
  .dw-container{padding:0 16px}
  .dw-faq__inner{padding:0 16px}
  .dw-sh__title{font-size:24px}
}

::-webkit-scrollbar{width:5px}
::-webkit-scrollbar-track{background:var(--dark)}
::-webkit-scrollbar-thumb{background:rgba(212,175,110,.2);border-radius:3px}
      `}</style>
    </div>
  );
}
